import { AdvisorBubble, Column, SignatureCanvas } from '@/components'
import { useSnackbar } from '@/providers'
import { useSignPoa } from '@/services'
import { useTranslation } from 'react-i18next'
import {
  BasicFormItem,
  FormDatepicker,
  FormInputField,
} from '../../../../FormItem'
import { PoaInputsRow } from '../../../styles'
import { StepLayout } from '../components'
import { useChecklistStepper } from '../hooks/useChecklistStepper'
import { poaValidationSchema } from '../validationSchema'
import { SignPoaFormValues, StepperFormValues } from '../utils/types'
import { UserProfile } from '@/types/user-profile'
import { useOutletContext } from 'react-router-dom'

const SignPoaForm = () => {
  const { t } = useTranslation()

  return (
    <Column gap="16px">
      <Column gap="4px">
        <BasicFormItem
          component={SignatureCanvas}
          name="signature"
          showErrorHelperText={false}
          noMargin
        />
        <PoaInputsRow>
          <FormInputField
            label={t('riskChecklist.businessInformationFlow.signPoa.name')}
            name="fullname"
            showValidCheck
          />
          <FormDatepicker
            label={t('riskChecklist.businessInformationFlow.signPoa.birthdate')}
            max={new Date()}
            name="birthdate"
            showCalendar={false}
          />
        </PoaInputsRow>
      </Column>
      <AdvisorBubble
        text={t(
          'riskChecklist.businessInformationFlow.signPoa.advisorBubbleText',
        )}
      />
    </Column>
  )
}

const makeInitialValues = (
  formValues: StepperFormValues,
  user: UserProfile,
) => {
  if (formValues.fullname && formValues.birthdate && formValues.signature) {
    return formValues
  }

  return {
    fullname: user.fullname || '',
    birthdate: user.birthdate,
    signature: '',
  }
}

interface OutletContext {
  user: UserProfile
}

interface SignPoaProps {
  companyId: string
}

export const SignPoa = ({ companyId }: SignPoaProps) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const signPoaMutation = useSignPoa(companyId)
  const { user } = useOutletContext<OutletContext>()
  const { goToNext, formValues } = useChecklistStepper()

  const handleSubmit = async (values: SignPoaFormValues) => {
    try {
      await signPoaMutation.mutateAsync({
        birthdate: values.birthdate,
        fullname: values.fullname,
        signature: values.signature,
      })
      goToNext(values)
    } catch (_e) {
      snackbar.error(t('snackbars.error.generalMsg'))
    }
  }

  return (
    <StepLayout
      title={t('riskChecklist.businessInformationFlow.signPoa.title')}
      subtitle={t('riskChecklist.businessInformationFlow.signPoa.subtitle')}
      onNext={handleSubmit}
      isLoading={signPoaMutation.isLoading}
      initialValues={makeInitialValues(formValues, user)}
      validationSchema={() =>
        poaValidationSchema(
          t('validations', {
            returnObjects: true,
          }),
        )
      }
      onSkip={() => goToNext({})}
    >
      <SignPoaForm />
    </StepLayout>
  )
}
